import React, { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Calendar as CalendarIcon,
  Filter,
  Trash2,
  FileText,
  X,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Download,
  RotateCcw,
} from "lucide-react";
import { Session, Room } from "@/types";
import { apiService } from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/useMediaQuery";
import MobileDayView from "@/components/sessions/MobileDayView";
import MobileSessionActionSheet from "@/components/sessions/MobileSessionActionSheet";

// Session status for calendar display
interface SessionStatus {
  color: string; // bg-color class
  label: string;
  priority: number; // Higher priority status shows when multiple apply
}

// Section summary type
interface SectionSummary {
  section_id: number | null;
  section_name: string;
  image_count: number;
  detection_count: number;
  status: 'no_upload' | 'not_validated' | 'validated';
}

interface SessionSummary {
  session_id: number;
  sections: SectionSummary[];
  total_detections: number;
  overall_status: 'not_started' | 'incomplete_sections' | 'awaiting_validation' | 'complete';
}

// Status definitions with smart color coding
const SESSION_STATUSES: Record<string, SessionStatus> = {
  not_started: {
    color: "bg-gray-100 border-gray-300",
    label: "Not Started",
    priority: 1,
  },
  past_not_created: {
    color: "bg-red-50 border-red-300",
    label: "Past - Not Created",
    priority: 0,
  },
  past_deadline: {
    color: "bg-red-100 border-red-400",
    label: "Past Deadline",
    priority: 5,
  },
  incomplete_sections: {
    color: "bg-blue-100 border-blue-400",
    label: "Images Uploaded",
    priority: 2,
  },
  awaiting_validation: {
    color: "bg-orange-100 border-orange-400",
    label: "Awaiting Validation",
    priority: 3,
  },
  complete: {
    color: "bg-green-100 border-green-400",
    label: "Complete",
    priority: 4,
  },
  report_generated: {
    color: "bg-purple-100 border-purple-400",
    label: "Report Generated",
    priority: 6,
  },
};

// Section status - dot colors only
const SECTION_DOT_COLORS: Record<string, string> = {
  no_upload: "bg-red-400",
  not_validated: "bg-amber-400",
  validated: "bg-emerald-400",
};

// Status border colors (left accent)
const STATUS_BORDER_COLORS: Record<string, string> = {
  not_started: "border-l-gray-300",
  past_not_created: "border-l-red-400",
  past_deadline: "border-l-red-500",
  incomplete_sections: "border-l-blue-400",
  awaiting_validation: "border-l-amber-400",
  complete: "border-l-emerald-400",
  report_generated: "border-l-purple-400",
};

// Helper to get week start (Monday) for a given date
function getWeekStart(date: Date): Date {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Adjust when day is Sunday
  return new Date(d.setDate(diff));
}

// Generate array of 7 days starting from Monday
function getWeekDays(weekStart: Date): Date[] {
  const days: Date[] = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    days.push(day);
  }
  return days;
}

// Format date as YYYY-MM-DD for comparison
function formatDateKey(date: Date): string {
  return date.toISOString().split("T")[0];
}

// Check if date is in the past
function isPastDate(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
}

// Check if date is in the future
function isFutureDate(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate > today;
}

// Check if date is today
function isToday(date: Date): boolean {
  const today = new Date();
  return formatDateKey(date) === formatDateKey(today);
}

// Context Menu Component
interface ContextMenuProps {
  x: number;
  y: number;
  session: Session | null;
  onClose: () => void;
  onDelete: () => void;
  onReopen: () => void;
  onGenerateReport: () => void;
  onViewDetails: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({
  x,
  y,
  session,
  onClose,
  onDelete,
  onReopen,
  onGenerateReport,
  onViewDetails,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  if (!session) return null;

  return (
    <div
      ref={menuRef}
      className="fixed bg-white border-2 border-gray-300 rounded-lg shadow-xl z-50 py-2 min-w-[180px]"
      style={{ left: x, top: y }}
    >
      <button
        onClick={onViewDetails}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <FileText className="h-4 w-4" />
        View Details
      </button>
      <button
        onClick={onReopen}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <RotateCcw className="h-4 w-4 text-blue-600" />
        Reopen (Delete & Create New)
      </button>
      <button
        onClick={onGenerateReport}
        className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
      >
        <FileText className="h-4 w-4 text-purple-600" />
        Generate Report
      </button>
      <div className="border-t border-gray-200 my-1"></div>
      <button
        onClick={onDelete}
        className="w-full px-4 py-2 text-left text-sm hover:bg-red-50 text-red-600 flex items-center gap-2"
      >
        <Trash2 className="h-4 w-4" />
        Delete
      </button>
    </div>
  );
};

const SessionsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // State
  const [sessions, setSessions] = useState<Session[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [sessionSummaries, setSessionSummaries] = useState<Record<string, SessionSummary>>({});

  // Week navigation - start with current week
  const [currentWeekStart, setCurrentWeekStart] = useState<Date>(
    getWeekStart(new Date())
  );

  // Day navigation for mobile - start with today
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());

  // Filtering
  const [showRoomFilter, setShowRoomFilter] = useState(false);
  const [filteredRoomIds, setFilteredRoomIds] = useState<Set<number>>(new Set());

  // Bulk selection
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedCells, setSelectedCells] = useState<Set<string>>(new Set());

  // Context menu
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number; session: Session | null; roomId: number; date: Date } | null>(null);

  // Mobile action sheet
  const [mobileActionSession, setMobileActionSession] = useState<Session | null>(null);

  // Session creation guard (prevents double-tap creating duplicate sessions)
  const [creatingSession, setCreatingSession] = useState(false);

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      const [sessionsResponse, roomsResponse] = await Promise.all([
        apiService.getSessions(),
        apiService.getRooms(),
      ]);
      setSessions(sessionsResponse.sessions);
      setRooms(roomsResponse.rooms);

      // Fetch summaries for sessions in current week
      const sessionIds = sessionsResponse.sessions.map((s: Session) => s.id);
      if (sessionIds.length > 0) {
        const summariesResponse = await apiService.getSessionSummaries(sessionIds);
        setSessionSummaries(summariesResponse.summaries);
      }
    } catch (error: any) {
      console.error("Failed to fetch data:", error);
      setError("Failed to load sessions and rooms");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Calculate week days
  const weekDays = useMemo(() => getWeekDays(currentWeekStart), [currentWeekStart]);

  // Group sessions by room and date
  const sessionsByRoomAndDate = useMemo(() => {
    const map = new Map<string, Session>();
    sessions.forEach((session) => {
      if (session.room_id && session.session_date) {
        const key = `${session.room_id}-${session.session_date}`;
        map.set(key, session);
      }
    });
    return map;
  }, [sessions]);

  // Calculate session status based on summary
  const getSessionStatus = (session: Session): SessionStatus => {
    const summary = sessionSummaries[String(session.id)];

    if (summary) {
      switch (summary.overall_status) {
        case 'not_started':
          return SESSION_STATUSES.not_started;
        case 'incomplete_sections':
          return SESSION_STATUSES.incomplete_sections;
        case 'awaiting_validation':
          return SESSION_STATUSES.awaiting_validation;
        case 'complete':
          return SESSION_STATUSES.complete;
      }
    }

    // Fallback to basic logic
    if (!session.image_count || session.image_count === 0) {
      return SESSION_STATUSES.not_started;
    }

    if (session.is_active) {
      return SESSION_STATUSES.incomplete_sections;
    }

    return SESSION_STATUSES.complete;
  };

  // Filter rooms
  const displayedRooms = useMemo(() => {
    if (filteredRoomIds.size === 0) {
      return rooms;
    }
    return rooms.filter((room) => filteredRoomIds.has(room.id));
  }, [rooms, filteredRoomIds]);

  // Week navigation handlers
  const goToPreviousWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() - 7);
    setCurrentWeekStart(newStart);
  };

  const goToNextWeek = () => {
    const newStart = new Date(currentWeekStart);
    newStart.setDate(newStart.getDate() + 7);
    setCurrentWeekStart(newStart);
  };

  const goToToday = () => {
    setCurrentWeekStart(getWeekStart(new Date()));
    setSelectedDate(new Date()); // Also sync mobile date
  };

  // Day navigation handlers for mobile
  const goToPreviousDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() - 1);

      // Update week if we've moved to a different week
      const newWeekStart = getWeekStart(newDate);
      if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
        setCurrentWeekStart(newWeekStart);
      }

      return newDate;
    });
  }, [currentWeekStart]);

  const goToNextDay = useCallback(() => {
    setSelectedDate((prev) => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + 1);

      // Update week if we've moved to a different week
      const newWeekStart = getWeekStart(newDate);
      if (newWeekStart.getTime() !== currentWeekStart.getTime()) {
        setCurrentWeekStart(newWeekStart);
      }

      return newDate;
    });
  }, [currentWeekStart]);

  const goToTodayMobile = useCallback(() => {
    const today = new Date();
    setSelectedDate(today);
    setCurrentWeekStart(getWeekStart(today));
  }, []);

  // Cell selection handlers - only allow selecting cells with sessions
  const toggleCellSelection = (roomId: number, dateKey: string) => {
    const key = `${roomId}-${dateKey}`;
    const session = sessionsByRoomAndDate.get(key);

    // Don't allow selecting cells without sessions
    if (!session) return;

    const newSelected = new Set(selectedCells);
    if (newSelected.has(key)) {
      newSelected.delete(key);
    } else {
      newSelected.add(key);
    }
    setSelectedCells(newSelected);
  };

  const clearSelection = () => {
    setSelectedCells(new Set());
    setSelectionMode(false);
  };

  const toggleSelectionMode = () => {
    const newMode = !selectionMode;
    setSelectionMode(newMode);
    if (!newMode) {
      setSelectedCells(new Set());
    }
  };

  // Create session handler
  const handleCreateSession = async (roomId: number, date: Date) => {
    // Prevent double-tap creating duplicate sessions
    if (creatingSession) {
      console.log('[SessionCreate] Blocked duplicate session creation attempt');
      return;
    }

    if (isFutureDate(date)) {
      alert("Cannot create sessions for future dates. Please select today or a past date.");
      return;
    }

    const isPast = isPastDate(date);

    if (isPast) {
      const confirmed = confirm(
        "You are creating a session for a past date. This will be marked as a retrospective inventory entry. Continue?"
      );
      if (!confirmed) return;
    }

    try {
      setCreatingSession(true);
      const newSession = await apiService.createSession({
        room_id: roomId,
        session_date: formatDateKey(date),
      });
      navigate(`/sessions/${newSession.id}`);
    } catch (error: any) {
      console.error("Failed to create session:", error);
      alert("Failed to create session");
    } finally {
      setCreatingSession(false);
    }
  };

  // Get selected sessions
  const getSelectedSessions = useCallback((): Session[] => {
    const sessions: Session[] = [];
    selectedCells.forEach((key) => {
      const session = sessionsByRoomAndDate.get(key);
      if (session) {
        sessions.push(session);
      }
    });
    return sessions;
  }, [selectedCells, sessionsByRoomAndDate]);

  // Bulk actions
  const handleBulkDelete = async () => {
    const sessions = getSelectedSessions();
    if (sessions.length === 0) {
      alert("No sessions selected");
      return;
    }

    if (!confirm(`Delete ${sessions.length} selected session${sessions.length > 1 ? 's' : ''}?`)) return;

    try {
      await Promise.all(
        sessions.map((session) => apiService.deleteSession(session.id))
      );
      await fetchData();
      clearSelection();
    } catch (error) {
      console.error("Failed to delete sessions:", error);
      alert("Failed to delete some sessions");
    }
  };

  // Reopen action - delete and create new session
  const handleReopen = async (session: Session) => {
    if (!confirm(`This will delete the current session and create a new one for the same date and room. All data will be lost. Continue?`)) return;

    try {
      const roomId = session.room_id;
      const sessionDate = session.session_date;

      // Delete the old session
      await apiService.deleteSession(session.id);

      // Create a new session
      const newSession = await apiService.createSession({
        room_id: roomId!,
        session_date: sessionDate,
      });

      navigate(`/sessions/${newSession.id}`);
    } catch (error) {
      console.error("Failed to reopen session:", error);
      alert("Failed to reopen session");
    }
  };

  const handleBulkReopen = async () => {
    const sessions = getSelectedSessions();
    if (sessions.length === 0) {
      alert("No sessions selected");
      return;
    }

    if (!confirm(`Reopen ${sessions.length} session${sessions.length > 1 ? 's' : ''}? This will delete each session and create new ones. All data will be lost.`)) return;

    try {
      for (const session of sessions) {
        const roomId = session.room_id;
        const sessionDate = session.session_date;
        await apiService.deleteSession(session.id);
        await apiService.createSession({
          room_id: roomId!,
          session_date: sessionDate,
        });
      }
      await fetchData();
      clearSelection();
    } catch (error) {
      console.error("Failed to reopen sessions:", error);
      alert("Failed to reopen some sessions");
    }
  };

  const handleBulkGenerateReport = async () => {
    const sessions = getSelectedSessions();
    if (sessions.length === 0) {
      alert("No sessions selected");
      return;
    }

    const completedSessions = sessions.filter(
      (session) => !session.is_active && session.image_count && session.image_count > 0
    );

    if (completedSessions.length === 0) {
      alert("No completed sessions selected. Only completed sessions can have reports generated.");
      return;
    }

    if (completedSessions.length !== sessions.length) {
      const incompleteCount = sessions.length - completedSessions.length;
      if (!confirm(
        `${incompleteCount} session${incompleteCount > 1 ? 's are' : ' is'} not completed and will be skipped.\n\nGenerate reports for ${completedSessions.length} completed session${completedSessions.length > 1 ? 's' : ''}?`
      )) {
        return;
      }
    }

    // Collect unique dates and rooms from selected sessions
    const dates = new Set<string>();
    const roomIds = new Set<number>();
    completedSessions.forEach(session => {
      if (session.session_date) dates.add(session.session_date);
      if (session.room_id !== undefined && session.room_id !== null) {
        roomIds.add(session.room_id);
      }
    });

    console.log('Bulk report - selected sessions:', completedSessions.map(s => ({ id: s.id, date: s.session_date, room_id: s.room_id })));
    console.log('Bulk report - rooms param:', Array.from(roomIds).join(','));

    // Build query params - use most recent date if multiple
    const params = new URLSearchParams();
    const sortedDates = Array.from(dates).sort((a, b) => b.localeCompare(a));
    if (sortedDates.length > 0) {
      params.set('date', sortedDates[0]); // Most recent date
    }
    if (roomIds.size > 0) {
      params.set('rooms', Array.from(roomIds).join(','));
    }

    // Navigate to unified reports page with params
    navigate(`/reports?${params.toString()}`);
    clearSelection();
  };

  const handleBulkExport = async () => {
    const sessions = getSelectedSessions();
    if (sessions.length === 0) {
      alert("No sessions selected");
      return;
    }

    try {
      const exportData = sessions.map((session) => ({
        id: session.id,
        room_name: session.room_name,
        session_date: session.session_date,
        is_retrospective: session.is_retrospective,
        image_count: session.image_count,
        is_active: session.is_active,
        created_at: session.created_at,
      }));

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sessions_export_${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      clearSelection();
    } catch (error) {
      console.error("Failed to export sessions:", error);
      alert("Failed to export sessions");
    }
  };

  // Handle cell click
  const handleCellClick = (roomId: number, date: Date, session: Session | undefined) => {
    const dateKey = formatDateKey(date);

    if (selectionMode) {
      // In selection mode - only toggle if there's a session
      if (session) {
        toggleCellSelection(roomId, dateKey);
      }
    } else {
      // Normal mode - navigate or create
      if (session) {
        navigate(`/sessions/${session.id}`);
      } else {
        handleCreateSession(roomId, date);
      }
    }
  };

  // Handle right-click context menu
  const handleContextMenu = (e: React.MouseEvent, roomId: number, date: Date, session: Session | undefined) => {
    if (!session) return;
    e.preventDefault();
    setContextMenu({
      x: e.clientX,
      y: e.clientY,
      session,
      roomId,
      date,
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const selectedSessions = getSelectedSessions();

  return (
    <div className="space-y-6">
      {/* Custom CSS */}
      <style>{`
        .cell-missed {
          background: linear-gradient(135deg, #fef7f7 0%, #fef2f2 100%);
        }
        .cell-missed::before {
          content: '';
          position: absolute;
          top: 6px;
          right: 6px;
          width: 6px;
          height: 6px;
          background: #fca5a5;
          border-radius: 50%;
        }
      `}</style>

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-serif font-bold text-gray-900">
            Inventory Sessions
          </h1>
          <p className="text-gray-600 hidden md:block">
            {selectionMode
              ? "Selection mode: Click session cells to select"
              : "Weekly calendar view - click cells to create or view sessions"
            }
          </p>
          <p className="text-gray-600 md:hidden">
            Swipe left/right to change days
          </p>
        </div>

        {/* Desktop action buttons - hidden on mobile */}
        <div className="hidden md:flex items-center gap-2">
          {/* Selection Mode Toggle */}
          <button
            onClick={toggleSelectionMode}
            className={`px-4 py-2 border rounded-lg flex items-center gap-2 transition-colors font-medium ${
              selectionMode
                ? "bg-orange-600 border-orange-600 text-white hover:bg-orange-700"
                : "bg-orange-50 border-orange-300 text-orange-700 hover:bg-orange-100"
            }`}
          >
            <CheckCircle className="h-4 w-4" />
            {selectionMode ? "Exit Selection" : "Select Multiple"}
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <p className="text-red-700">{error}</p>
        </div>
      )}

      {/* Week Navigation / Bulk Actions Bar - Desktop only */}
      <div className={`hidden md:flex items-center justify-between rounded-lg p-4 ${
        selectionMode && selectedSessions.length > 0
          ? "bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg"
          : "bg-white border border-gray-300"
      }`}>
        {selectionMode && selectedSessions.length > 0 ? (
          // Bulk Actions Mode
          <>
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full px-4 py-2">
                <span className="font-bold text-lg">{selectedSessions.length}</span>
                <span className="ml-2 font-medium">
                  session{selectedSessions.length !== 1 ? "s" : ""} selected
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={handleBulkReopen}
                className="px-3 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-md text-sm"
                title="Reopen selected sessions (delete & create new)"
              >
                <RotateCcw className="h-4 w-4" />
                Reopen
              </button>

              <button
                onClick={handleBulkGenerateReport}
                className="px-3 py-2 bg-purple-600 hover:bg-purple-700 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-md text-sm"
                title="Generate reports for selected sessions"
              >
                <FileText className="h-4 w-4" />
                Reports
              </button>

              <button
                onClick={handleBulkExport}
                className="px-3 py-2 bg-indigo-600 hover:bg-indigo-700 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-md text-sm"
                title="Export selected sessions as JSON"
              >
                <Download className="h-4 w-4" />
                Export
              </button>

              <button
                onClick={handleBulkDelete}
                className="px-3 py-2 bg-red-600 hover:bg-red-700 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-md text-sm"
                title="Delete selected sessions"
              >
                <Trash2 className="h-4 w-4" />
                Delete
              </button>

              <button
                onClick={clearSelection}
                className="px-3 py-2 bg-white text-gray-900 hover:bg-gray-100 rounded-lg flex items-center gap-2 transition-colors font-medium shadow-md text-sm"
                title="Clear selection"
              >
                <X className="h-4 w-4" />
                Cancel
              </button>
            </div>
          </>
        ) : (
          // Week Navigation Mode
          <>
            <button
              onClick={goToPreviousWeek}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Previous week"
            >
              <ChevronLeft className="h-5 w-5 text-gray-700" />
            </button>

            <div className="flex items-center gap-4">
              <button
                onClick={goToToday}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors flex items-center gap-2"
              >
                <CalendarIcon className="h-4 w-4" />
                Today
              </button>
              <span className="text-lg font-semibold text-gray-900">
                Week of {weekDays[0].toLocaleDateString("en-US", { month: "short", day: "numeric" })} -{" "}
                {weekDays[6].toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </span>
            </div>

            <button
              onClick={goToNextWeek}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title="Next week"
            >
              <ChevronRight className="h-5 w-5 text-gray-700" />
            </button>
          </>
        )}
      </div>

      {/* Mobile Day View */}
      <div className="md:hidden -mx-4 sm:-mx-6">
        <MobileDayView
          selectedDate={selectedDate}
          rooms={displayedRooms}
          sessionsByRoomAndDate={sessionsByRoomAndDate}
          sessionSummaries={sessionSummaries as Record<number, SessionSummary>}
          onPreviousDay={goToPreviousDay}
          onNextDay={goToNextDay}
          onToday={goToTodayMobile}
          onSessionClick={(session) => navigate(`/sessions/${session.id}`)}
          onCreateSession={handleCreateSession}
          onSessionLongPress={(session) => setMobileActionSession(session)}
          selectionMode={selectionMode}
          selectedCells={selectedCells}
          onToggleCellSelection={toggleCellSelection}
        />
      </div>

      {/* Calendar Grid - Desktop View */}
      <div className="hidden md:block bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
        {/* Header Row - Days of Week */}
        <div className="grid grid-cols-8 border-b border-gray-200 bg-gray-50/80">
          <div className="p-3 border-r border-gray-200">
            {/* Room Filter Dropdown */}
            <div className="relative">
              <button
                onClick={() => setShowRoomFilter(!showRoomFilter)}
                className={`w-full px-2.5 py-1.5 text-xs border rounded-lg flex items-center justify-between gap-1.5 transition-colors ${
                  filteredRoomIds.size > 0
                    ? "bg-primary-50 border-primary-200 text-primary-700"
                    : "bg-white border-gray-200 text-gray-600 hover:border-gray-300"
                }`}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <Filter className="h-3 w-3 flex-shrink-0" />
                  <span className="truncate font-medium">
                    {filteredRoomIds.size > 0 ? `${filteredRoomIds.size} selected` : 'Rooms'}
                  </span>
                </div>
                {filteredRoomIds.size > 0 && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setFilteredRoomIds(new Set());
                    }}
                    className="flex-shrink-0 p-0.5 hover:bg-gray-200 rounded transition-colors"
                    title="Clear filter"
                  >
                    <X className="h-3 w-3" />
                  </button>
                )}
              </button>

              {/* Dropdown */}
              {showRoomFilter && (
                <>
                  <div
                    className="fixed inset-0 z-10"
                    onClick={() => setShowRoomFilter(false)}
                  />
                  <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-lg z-20 min-w-[180px] max-w-[280px]">
                    <div className="p-2.5 border-b border-gray-100">
                      <div className="text-xs font-medium text-gray-500">Filter by room</div>
                    </div>
                    <div className="max-h-56 overflow-y-auto p-1.5">
                      {rooms.map((room) => (
                        <label
                          key={room.id}
                          className="flex items-center gap-2 hover:bg-gray-50 px-2.5 py-2 rounded-lg cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={filteredRoomIds.has(room.id)}
                            onChange={(e) => {
                              const newSet = new Set(filteredRoomIds);
                              if (e.target.checked) {
                                newSet.add(room.id);
                              } else {
                                newSet.delete(room.id);
                              }
                              setFilteredRoomIds(newSet);
                            }}
                            className="h-3.5 w-3.5 text-primary-600 border-gray-300 rounded"
                          />
                          <span className="text-sm text-gray-700">{room.name}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
          {weekDays.map((day, index) => {
            const isSunday = index === 6;
            const todayHighlight = isToday(day);
            return (
              <div
                key={index}
                className={`py-3 px-2 text-center border-r border-gray-200 last:border-r-0 ${
                  isSunday ? "text-gray-400" : todayHighlight ? "bg-blue-50/50" : ""
                }`}
              >
                <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">
                  {day.toLocaleDateString("en-US", { weekday: "short" })}
                </div>
                <div className={`text-xl font-semibold mt-0.5 ${todayHighlight ? "text-blue-600" : "text-gray-800"}`}>
                  {day.getDate()}
                </div>
              </div>
            );
          })}
        </div>

        {/* Room Rows */}
        {displayedRooms.map((room) => (
          <div key={room.id} className="grid grid-cols-8 border-b border-gray-100 last:border-b-0">
            {/* Room Name */}
            <div className="p-3 bg-gray-50/50 font-medium text-sm text-gray-700 border-r border-gray-200 flex items-center">
              <div className="truncate">{room.name}</div>
            </div>

            {/* Day Cells */}
            {weekDays.map((day, dayIndex) => {
              const isSunday = dayIndex === 6;
              const isFuture = isFutureDate(day);
              const dateKey = formatDateKey(day);
              const cellKey = `${room.id}-${dateKey}`;
              const session = sessionsByRoomAndDate.get(cellKey);
              const isSelected = selectedCells.has(cellKey);
              const isPast = isPastDate(day);
              const todayCell = isToday(day);
              const summary = session ? sessionSummaries[String(session.id)] : null;
              const isPastNotCreated = isPast && !session && !isSunday;

              // Determine status key for border color
              const statusKey = summary?.overall_status || (session ? (session.is_active ? 'incomplete_sections' : 'complete') : 'not_started');

              return (
                <div
                  key={dayIndex}
                  onClick={() => !isSunday && !isFuture && handleCellClick(room.id, day, session)}
                  onContextMenu={(e) => handleContextMenu(e, room.id, day, session)}
                  className={`
                    border-r border-gray-200 last:border-r-0 min-h-[110px] transition-all relative
                    ${isSunday ? "bg-gray-50/50" : ""}
                    ${isFuture ? "bg-gray-50/30" : ""}
                    ${isPastNotCreated ? "cell-missed" : ""}
                    ${session ? `bg-white border-l-4 ${STATUS_BORDER_COLORS[statusKey] || 'border-l-gray-300'}` : ""}
                    ${!session && !isSunday && !isFuture && !isPast ? "bg-white hover:bg-gray-50" : ""}
                    ${todayCell && !session && !isPastNotCreated ? "bg-blue-50/40" : ""}
                    ${isSelected ? "ring-2 ring-primary-500 ring-inset bg-primary-50/50" : ""}
                    ${selectionMode && !session ? "opacity-40" : ""}
                    ${!isSunday && !isFuture ? "cursor-pointer" : "cursor-default"}
                  `}
                >
                  {/* Session exists */}
                  {session && !isSunday && (
                    <div className="p-2 h-full flex flex-col">
                      {/* Total detections - prominent */}
                      <div className="text-lg font-bold text-gray-800 leading-tight">
                        {summary?.total_detections ?? 0}
                        <span className="text-[10px] font-normal text-gray-500 ml-1">items</span>
                      </div>

                      {/* Section breakdown - compact */}
                      {summary && summary.sections.length > 0 && (
                        <div className="mt-1.5 space-y-1 flex-1 overflow-hidden">
                          {summary.sections.slice(0, 3).map((sec, idx) => (
                            <div key={idx} className="flex items-center gap-1.5 text-[11px] text-gray-600">
                              <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${SECTION_DOT_COLORS[sec.status]}`}></span>
                              <span className="truncate">{sec.section_name}</span>
                              <span className="text-gray-400 ml-auto flex-shrink-0">{sec.detection_count}</span>
                            </div>
                          ))}
                          {summary.sections.length > 3 && (
                            <div className="text-[10px] text-gray-400">
                              +{summary.sections.length - 3} more
                            </div>
                          )}
                        </div>
                      )}

                      {/* Retrospective badge */}
                      {session.is_retrospective && (
                        <div className="mt-auto pt-1">
                          <span className="text-[9px] uppercase tracking-wide font-semibold text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                            Retro
                          </span>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sunday */}
                  {!session && isSunday && (
                    <div className="h-full flex items-center justify-center">
                      <span className="text-xs text-gray-300 font-medium">—</span>
                    </div>
                  )}

                  {/* Future */}
                  {!session && isFuture && !isSunday && (
                    <div className="h-full flex items-center justify-center">
                      <span className="text-xs text-gray-300">—</span>
                    </div>
                  )}

                  {/* Past - not created (has red dot from CSS) */}
                  {isPastNotCreated && (
                    <div className="h-full flex items-center justify-center p-2">
                      <span className="text-xs text-red-400 font-medium">Missed</span>
                    </div>
                  )}

                  {/* Today or past - can create */}
                  {!session && !isSunday && !isFuture && !isPast && (
                    <div className="h-full flex items-center justify-center group">
                      <div className="w-8 h-8 rounded-full border-2 border-dashed border-gray-300 group-hover:border-primary-400 group-hover:bg-primary-50 flex items-center justify-center transition-all">
                        <span className="text-gray-400 group-hover:text-primary-500 text-lg leading-none">+</span>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}

        {displayedRooms.length === 0 && (
          <div className="p-12 text-center text-gray-500">
            No rooms available. Create rooms in the admin panel.
          </div>
        )}
      </div>

      {/* Legend - Desktop only */}
      <div className="hidden md:flex items-center justify-center gap-8 py-4 px-6 bg-gray-50 rounded-lg border border-gray-200">
        {/* Session Status */}
        <div className="flex items-center gap-6">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-emerald-400"></div>
              <span className="text-sm text-gray-600">Complete</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-amber-400"></div>
              <span className="text-sm text-gray-600">Awaiting validation</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-blue-400"></div>
              <span className="text-sm text-gray-600">In progress</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-1 h-4 rounded-full bg-gray-300"></div>
              <span className="text-sm text-gray-600">Not started</span>
            </div>
          </div>
        </div>

        {/* Separator */}
        <div className="w-px h-6 bg-gray-300"></div>

        {/* Section Status */}
        <div className="flex items-center gap-6">
          <span className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Sections</span>
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-emerald-400"></span>
              <span className="text-sm text-gray-600">Validated</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400"></span>
              <span className="text-sm text-gray-600">Pending</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-red-400"></span>
              <span className="text-sm text-gray-600">No images</span>
            </div>
          </div>
        </div>
      </div>

      {/* Context Menu */}
      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          session={contextMenu.session}
          onClose={() => setContextMenu(null)}
          onViewDetails={() => {
            if (contextMenu.session) {
              navigate(`/sessions/${contextMenu.session.id}`);
            }
            setContextMenu(null);
          }}
          onDelete={async () => {
            if (contextMenu.session) {
              if (confirm("Delete this session?")) {
                try {
                  await apiService.deleteSession(contextMenu.session.id);
                  await fetchData();
                } catch (error) {
                  alert("Failed to delete session");
                }
              }
            }
            setContextMenu(null);
          }}
          onReopen={async () => {
            if (contextMenu.session) {
              await handleReopen(contextMenu.session);
            }
            setContextMenu(null);
          }}
          onGenerateReport={() => {
            if (contextMenu.session) {
              const date = contextMenu.session.session_date;
              const roomId = contextMenu.session.room_id;
              const params = new URLSearchParams();
              params.set('date', date);
              if (roomId) params.set('rooms', roomId.toString());
              navigate(`/reports?${params.toString()}`);
            } else {
              navigate('/reports');
            }
            setContextMenu(null);
          }}
        />
      )}

      {/* Mobile Action Sheet */}
      <MobileSessionActionSheet
        isOpen={!!mobileActionSession}
        session={mobileActionSession}
        onClose={() => setMobileActionSession(null)}
        onViewDetails={() => {
          if (mobileActionSession) {
            navigate(`/sessions/${mobileActionSession.id}`);
          }
        }}
        onReopen={async () => {
          if (mobileActionSession) {
            await handleReopen(mobileActionSession);
            setMobileActionSession(null);
          }
        }}
        onGenerateReport={() => {
          if (mobileActionSession) {
            const date = mobileActionSession.session_date;
            const roomId = mobileActionSession.room_id;
            const params = new URLSearchParams();
            params.set('date', date);
            if (roomId) params.set('rooms', roomId.toString());
            navigate(`/reports?${params.toString()}`);
          } else {
            navigate('/reports');
          }
        }}
        onDelete={async () => {
          if (mobileActionSession) {
            if (confirm("Are you sure you want to delete this session? This action cannot be undone.")) {
              try {
                await apiService.deleteSession(mobileActionSession.id);
                await fetchData();
              } catch (error) {
                alert("Failed to delete session");
              }
            }
            setMobileActionSession(null);
          }
        }}
      />
    </div>
  );
};

export default SessionsPage;
